import { defineStore } from 'pinia';
import type { Token, User } from '@taxi/shared';
import { ApiException } from '@common/libs/caller';
import { useTokenStore } from './TokenStore';

export interface UserState {
	me: User | null;
	/** Vrai dès la première tentative de chargement, réussie ou non. */
	firstCalled: boolean;
	loading: boolean;
}

/**
 * Compte connecté et parcours d'authentification.
 *
 * Le profil chauffeur est renvoyé par `/auth/me` : le front sait donc dès
 * l'ouverture s'il doit démarrer en mode conduite, sans requête supplémentaire.
 */
export const useUserStore = defineStore('user', {
	state: (): UserState => ({
		me: null,
		firstCalled: false,
		loading: false,
	}),

	getters: {
		isDriver: (state): boolean => Boolean(state.me?.driver),
		displayName: (state): string =>
			`${state.me?.firstName ?? ''} ${state.me?.lastName ?? ''}`.trim(),
		initials: (state): string =>
			`${state.me?.firstName?.[0] ?? ''}${state.me?.lastName?.[0] ?? ''}`.toUpperCase(),
	},

	actions: {
		/**
		 * Charge le profil de l'utilisateur connecté.
		 *
		 * Les sessions **sans compte** — invité et borne — sont écartées d'emblée :
		 * elles n'ont pas de profil à charger, et solliciter `/auth/me` avec un tel
		 * jeton produirait un refus de droits parfaitement attendu.
		 */
		async load(): Promise<User | null> {
			const tokenStore = useTokenStore();

			if (!tokenStore.isAuthenticated || tokenStore.isKiosk || tokenStore.isGuest) {
				this.me = null;
				this.firstCalled = true;
				return null;
			}

			this.loading = true;
			try {
				this.me = await this.$caller().get<User>('/auth/me', { silentError: true });
				return this.me;
			} catch (error) {
				this.me = null;

				// Seul un jeton refusé justifie de vider la session. Une panne réseau
				// ou un refus de droits ne doit pas déconnecter : l'utilisateur
				// perdrait sa course en cours pour une erreur passagère.
				if (error instanceof ApiException && error.isUnauthorized) {
					tokenStore.setToken(null);
				}
				return null;
			} finally {
				this.loading = false;
				this.firstCalled = true;
			}
		},

		async login(email: string, password: string): Promise<User | null> {
			const token = await this.$caller().post<Token>(
				'/auth/login',
				{ email, password },
				{ silentError: true },
			);
			useTokenStore().setToken(token);
			return this.load();
		},

		/**
		 * Inscription.
		 *
		 * `claimToken` rattache au nouveau compte une course commandée sans
		 * inscription — c'est la conversion du parcours borne → mobile.
		 */
		async register(payload: Record<string, unknown>): Promise<User | null> {
			const token = await this.$caller().post<Token>('/auth/register', payload, {
				silentError: true,
			});
			useTokenStore().setToken(token);
			return this.load();
		},

		/**
		 * Demande un code de connexion par SMS.
		 *
		 * Même geste pour s'inscrire et se reconnecter : un voyageur n'a pas à
		 * savoir s'il a déjà un compte, et l'API ne le lui dit pas non plus.
		 */
		async startPhoneAuth(countryCode: string, mobileNumber: string): Promise<number> {
			const result = await this.$caller().post<{ expiresInSeconds: number }>(
				'/auth/phone/start',
				// Indicatif omis plutôt qu'envoyé vide : le numéro le porte déjà sous
				// sa forme internationale, et une chaîne vide serait refusée par la
				// validation de l'API.
				{ countryCode: countryCode || undefined, mobileNumber },
				{ silentError: true },
			);
			return result.expiresInSeconds;
		},

		/** Vérifie le code reçu : ouvre la session, et crée le compte si besoin. */
		async verifyPhoneAuth(payload: Record<string, unknown>): Promise<User | null> {
			const token = await this.$caller().post<Token>('/auth/phone/verify', payload, {
				silentError: true,
			});
			useTokenStore().setToken(token);
			return this.load();
		},

		/** Session anonyme : commander sans compte, l'inscription restant proposée. */
		async guest(name?: string, phone?: string): Promise<void> {
			const token = await this.$caller().post<Token>('/auth/guest', { name, phone });
			useTokenStore().setToken(token);
			this.me = null;
			this.firstCalled = true;
		},

		/**
		 * Ouvre la session machine d'une borne.
		 *
		 * Le jeton est lu dans la query string au chargement (`?kiosk=…`) : c'est
		 * l'unique élément à provisionner sur l'écran, la position et la langue
		 * venant ensuite de la base.
		 */
		async kioskSession(token: string): Promise<void> {
			const session = await this.$caller().post<Token>('/auth/kiosk', { token });
			useTokenStore().setToken(session);
			this.me = null;
			this.firstCalled = true;
		},

		async logout(): Promise<void> {
			const tokenStore = useTokenStore();
			try {
				await this.$caller().post('/auth/logout', {
					refreshToken: tokenStore.token?.refreshToken,
				});
			} catch {
				// Une déconnexion doit aboutir même si le réseau est coupé.
			}
			this.me = null;
			tokenStore.logout();
		},
	},
});
